/**
 * 「二次输入名称」确认框 —— 移除成员 / 解散账本**共用**（L 域 §5.6 · `AC-L5.13` / `AC-L5.9`）
 *
 * 依据：`RemoveMemberRequestSchema` / `DissolveLedgerRequestSchema`（🔴 服务端**校验名称相等**，
 * 不等即 400）· L 域 PRD §5.5.5「不可逆动作的确认强度」。
 *
 * ## 🔴 为什么必须"输入名称"而不是"再点一次确认"
 *
 * 这两个动作**不可逆**（移除 = 对方带着数据去新账本；解散 = 全家拆成多本）。
 * 只弹"确定吗"的确认，在注意力分散时等于没有 —— 输入名字要求用户**先读到**目标是谁 / 哪本账本。
 * `DR-018` 明确**不设冷静期**（拆分是分家不是删除），所以确认强度就是唯一的防线。
 *
 * ## 🔴 端侧**不比对**输入内容
 *
 * "必须与目标名称完全相同"是**服务端规则**（400）。端侧只判"非空才可点"（可用性）。
 * 在这里再比一遍 = 同一规则两处实现，且服务端改规则时端侧会**静默不跟**（铁律 79）。
 * 用户输错 ⇒ 服务端 400 ⇒ 端侧**原样展示 `message`**。
 *
 * ## ⚠️ 内容层单独导出（`ConfirmByNameBody`）
 *
 * MUI `Dialog` 走 `Portal`，在 `renderToStaticMarkup` 下内容拿不到（㉝/`60`/`65` 的同一课）。
 * ⇒ 内容层零 Portal、纯受控，供静态断言；外壳只是 `Dialog`。
 */

import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import type { ReactElement } from 'react';

export interface ConfirmByNameBodyProps {
  /** 动作名（「移除成员」/「解散账本」） */
  title: string;
  /** 后果说明（**必须说清"数据去哪了"** —— 见 `collab-labels.ts` 的两段措辞取舍） */
  description: string;
  /** 输入框标签（「再次输入这位成员的姓名」/「再次输入账本名称」） */
  inputLabel: string;
  /** 目标名称 —— 🔴 **只用于占位提示**，不参与校验（校验在服务端） */
  expectedHint: string;
  value: string;
  onChange: (v: string) => void;
  busy: boolean;
  /** 服务端错误原文（400 / 403 / 409）—— 端侧不翻译、不猜类型 */
  error: string | null;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  /** 静态断言锚点（每个宿主不同：`remove-member` / `dissolve-ledger`） */
  testId: string;
}

/** 内容层（零 Portal · 纯受控） */
export function ConfirmByNameBody(props: ConfirmByNameBodyProps): ReactElement {
  const {
    title,
    description,
    inputLabel,
    expectedHint,
    value,
    onChange,
    busy,
    error,
    confirmLabel,
    onCancel,
    onConfirm,
    testId,
  } = props;

  // 🔴 只判非空（可用性）—— 不比对内容（规则在服务端，见文件头）
  const canSubmit = !busy && value.trim().length > 0;

  return (
    <Box data-testid={testId}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {description}
      </Typography>

      <TextField
        size="small"
        fullWidth
        label={inputLabel}
        placeholder={expectedHint}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        disabled={busy}
        data-testid={`${testId}-input`}
      />

      {error !== null && (
        <Alert severity="error" sx={{ mt: 1.5 }} data-testid={`${testId}-error`}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button onClick={onCancel} disabled={busy} data-testid={`${testId}-cancel`}>
          取消
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={!canSubmit}
          data-testid={`${testId}-confirm`}
        >
          {busy ? '处理中…' : confirmLabel}
        </Button>
      </Stack>
    </Box>
  );
}
